import OutsideAlerter from '../outsideAlerter/outsideAlerter'
import Dropdown from '../ui/dropdown/dropdown'
import { useEffect, useState, useRef, useCallback } from 'react'
import usersStore from '../../store/users'
import Loader from '../loader/loader'
import dashbordStore from '../../store/dashbord'
import stageStore from '../../store/stage'
import { emmiter } from '../../helpers/Emmiter'
import { observer } from 'mobx-react-lite'

// Глобальное хранилище для отслеживания pending обновлений статусов drawings
// Ключ: positionStageId, значение: Map<drawingId, { status, timestamp }>
const pendingStatusUpdates = new Map()

function CellPickerHOC(renderItem, Wrapped) {
  return function CellPicker({
    getData,
    currentData,
    debouncedGetData,
    data,
    stageId,
    isCurrentStage,
    onSetData,
    blocked,
    small,
    positionStageId,
    boxCount,
    boxCountCheckCallback,
    drawings,
    currentDrawingId,
    ...props
  }) {
    const { selectedPositionsStages } = dashbordStore
    const { currentStage } = stageStore
    const dropdownRef = useRef(null)
    const [isDropDown, setIsDropDown] = useState(false)
    const [current, setCurrent] = useState(currentData)
    const [isDisabled, setIsDisabled] = useState(true)
    const {
      currentUser: { role }
    } = usersStore
    const isRoleFitStage = role.find(x => x.godmod) || role.find(x => x.stage?.id === stageId)

    const onSetGroupStatus = useCallback(
      args => {
        const selectedPositionsIDs = selectedPositionsStages.map(el => el.id)
        if (selectedPositionsIDs.includes(positionStageId)) {
          debouncedGetData()
          setCurrent(args)
        }
      },
      [selectedPositionsStages, debouncedGetData]
    )

    useEffect(() => {
      emmiter.on('setGroupStatus', onSetGroupStatus)
      return () => emmiter.off('setGroupStatus')
    }, [onSetGroupStatus])

    useEffect(() => {
      let cause = false

      if (!isRoleFitStage) {
        cause = 'Недостаточная роль'
      }

      if (!isCurrentStage) {
        cause = 'Невозможно внести изменения вне текущего этапа'
      }

      if (current?.additionalWorkTrigger && blocked) {
        cause = 'Позиция заблокирована на допработах'
      }

      setIsDisabled(cause)
    }, [
      isCurrentStage,
      isRoleFitStage,
      blocked
      // current
    ])

    //не показываем выбранный элемент в дропдауне TODO
    const filteredData = data || data.filter(x => x.id !== currentData?.id)

    useEffect(() => {
      setCurrent(currentData)
    }, [currentData])

    useEffect(() => {
      const scrollableDashbord = dropdownRef.current.closest('.js-scrollable-dashbord')
      const handler = () => {
        if (isDropDown) {
          setIsDropDown(false)
        }
      }
      scrollableDashbord?.addEventListener('scroll', handler)
      document.addEventListener('scroll', handler)
      return () => {
        scrollableDashbord?.removeEventListener('scroll', handler)
      }
    }, [isDropDown])

    const openDropdown = ev => {
      if (isDropDown) {
        return setIsDropDown(false)
      }

      const dropdownHeight = dropdownRef.current.getBoundingClientRect().height
      let { x, y, height, width } = ev.currentTarget.getBoundingClientRect()
      const modal = ev.currentTarget.closest('.ReactModal__Content')

      let top

      if (window.innerHeight - (y + height) > dropdownHeight) {
        top = y + height
      } else {
        top = y - dropdownHeight
      }

      if (!filteredData.length) return

      if (modal) {
        const modalBox = modal.getBoundingClientRect()
        x -= modalBox.x
        top -= modalBox.y
      }

      setIsDropDown({
        left: x,
        top,
        width
      })
    }

    const closeDropdown = useCallback(() => {
      setIsDropDown(false)
    }, [])

    const onChooseHanlder = async data => {
      const {isCheckBoxCount, isPrintCompleted} = data
      setIsDropDown(false)
      const isNoBoxCount = boxCount === null
      // Проверка для этапа "Сборка" (без drawings) - простая проверка без pending обновлений
      if(!!drawings && !drawings.length && isCheckBoxCount && isNoBoxCount) {
        try {
          // Ждем успешного сохранения количества коробок
          await boxCountCheckCallback()
          // После успешного сохранения продолжаем выполнение - обновляем статус
        } catch (error) {
          // Если пользователь закрыл модальное окно без сохранения или произошла ошибка, прерываем выполнение
          return
        }
      }
      
      // Логика для статусов drawings (этап печати)
      if (currentDrawingId && drawings) {
        // Проверка для этапа "Печать" с одним методом печати - простая проверка без pending обновлений
        // Аналогично этапу "Сборка"
        if (drawings.length === 1 && isCheckBoxCount && isNoBoxCount) {
          try {
            // Ждем успешного сохранения количества коробок
            await boxCountCheckCallback()
            // После успешного сохранения продолжаем выполнение - обновляем статус
            // Пропускаем дальнейшие проверки и сразу обновляем статус
            setCurrent(data)
            try {
              await onSetData(data.id, data)
            } catch (error) {
              throw error
            }
            return
          } catch (error) {
            // Если пользователь закрыл модальное окно без сохранения или произошла ошибка, прерываем выполнение
            return
          }
        }
        
        // Регистрируем pending обновление перед проверкой
        if (positionStageId) {
          if (!pendingStatusUpdates.has(positionStageId)) {
            pendingStatusUpdates.set(positionStageId, new Map())
          }
          const positionPendingUpdates = pendingStatusUpdates.get(positionStageId)
          positionPendingUpdates.set(currentDrawingId, {
            status: data,
            timestamp: Date.now()
          })
        }
        
        // Обновляем drawings оптимистично перед проверкой
        const updatedDrawings = drawings.map(d => 
          d.id === currentDrawingId 
            ? { ...d, status: data }
            : d
        )

        // Проверяем, является ли это последний статус "Метод готов" для этапа печати
        if (isPrintCompleted) {
          // Получаем pending обновления для текущей позиции
          const positionPendingUpdates = pendingStatusUpdates.get(positionStageId)
          
          // Проверяем, все ли остальные drawings уже имеют статус isPrintCompleted
          // Учитываем как текущее состояние, так и pending обновления
          const otherDrawings = updatedDrawings.filter(d => d.id !== currentDrawingId)
          
          const allOtherReady = otherDrawings.every(d => {
            // Проверяем текущий статус
            const currentStatusReady = d.status && d.status.isPrintCompleted
            
            // Проверяем pending обновление для этого drawing
            const pendingUpdate = positionPendingUpdates?.get(d.id)
            const pendingStatusReady = pendingUpdate?.status?.isPrintCompleted
            
            // Drawing готов, если либо текущий статус готов, либо есть pending обновление со статусом готов
            return currentStatusReady || pendingStatusReady
          })
          
          // Если это последний статус и нужно проверить количество коробок
          if (allOtherReady && isCheckBoxCount && isNoBoxCount) {
            // Очищаем pending обновления перед показом модального окна
            if (positionPendingUpdates) {
              positionPendingUpdates.delete(currentDrawingId)
            }
            try {
              // Ждем успешного сохранения количества коробок
              await boxCountCheckCallback()
              // После успешного сохранения продолжаем выполнение - обновляем статус
            } catch (error) {
              // Если пользователь закрыл модальное окно без сохранения или произошла ошибка, прерываем выполнение
              return
            }
          }
        }
      }
      
      // Если проверки пройдены, делаем запрос на бэкенд
      setCurrent(data)
      try {
        await onSetData(data.id, data)
        // Очищаем pending обновление после успешного запроса (только для drawings)
        if (positionStageId && currentDrawingId) {
          setTimeout(() => {
            const positionPendingUpdates = pendingStatusUpdates.get(positionStageId)
            if (positionPendingUpdates) {
              positionPendingUpdates.delete(currentDrawingId)
              // Если больше нет pending обновлений для этой позиции, удаляем запись
              if (positionPendingUpdates.size === 0) {
                pendingStatusUpdates.delete(positionStageId)
              }
            }
          }, 1500) // Даем время на обновление данных через getData
        }
      } catch (error) {
        // В случае ошибки также очищаем pending обновление (только для drawings)
        if (positionStageId && currentDrawingId) {
          const positionPendingUpdates = pendingStatusUpdates.get(positionStageId)
          if (positionPendingUpdates) {
            positionPendingUpdates.delete(currentDrawingId)
            if (positionPendingUpdates.size === 0) {
              pendingStatusUpdates.delete(positionStageId)
            }
          }
        }
        throw error // Пробрасываем ошибку дальше
      }
    }

    const renderItemWithHandler = renderItem(onChooseHanlder, {
      ...props,
      getData,
      drawings,
      currentDrawingId
    })

    return (
      <OutsideAlerter
        onClick={ev => ev.stopPropagation()}
        onEvent={closeDropdown}
        data-tooltip-hidden={!isDisabled}
        data-tooltip-id={'cell-click-alert'}
        data-tooltip-content={isDisabled}
        data-tooltip-place='top'
        data-tooltip-delay-hide={1000}
        className={`
					w-[100%] flex self-stretch
				`}>
        <div
          onClick={openDropdown}
          className={`
						${isDisabled ? 'pointer-events-none' : 'hover:cursor-pointer'} 
						${isDropDown ? 'shadow-default' : ''}
						${small ? 'text-Regular(12_14) ' : 'text-Regular(16_18)'}
						relative 
						w-[100%]
					`}>
          {/*{changingStatusId && positionStageId === changingStatusId &&*/}
          {/*	<div className={`absolute top-[50%] translate-y-[-50%] right-[22px]`}>*/}
          {/*		<Loader/>*/}
          {/*	</div>*/}
          {/*}*/}
          <Wrapped
            current={current}
            isDisabled={isDisabled}
            isDropDown={isDropDown}
            small={small}
            debouncedGetData={debouncedGetData}
            blocked={blocked}
            {...props}></Wrapped>
          <div
            ref={dropdownRef}
            className={`
							${isDropDown ? 'block' : 'd-none pointer-events-none  left-0 top-0'}
							fixed z-30 min-w-[200px]
						`}
            style={{
              left: isDropDown.left,
              top: isDropDown.top,
              width: isDropDown.width
            }}>
            <Dropdown positon='static' active={!!isDropDown} data={filteredData}>
              {renderItemWithHandler}
            </Dropdown>
          </div>
        </div>
      </OutsideAlerter>
    )
  }
}

export default CellPickerHOC
